import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useGetMe, type User } from "@workspace/api-client-react";
import { setAuthTokenGetter } from "@workspace/api-client-react/custom-fetch";
import { useLocation } from "wouter";

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (token: string) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

if (typeof window !== "undefined" && typeof localStorage !== "undefined") {
  setAuthTokenGetter(() => localStorage.getItem("studyflow_token"));
}

const originalFetch = typeof window !== "undefined" ? window.fetch.bind(window) : undefined;
if (typeof window !== "undefined" && originalFetch) {
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const response = await originalFetch(input, init);

    if (response.status === 401) {
      const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
      const isApiCall = url.startsWith("/api") || url.includes("/api/");
      const isLoginCall = url.includes("/auth/login") || url.includes("/auth/register");
      if (isApiCall && !isLoginCall) {
        localStorage.removeItem("studyflow_token");
        window.dispatchEvent(new Event("auth-unauthorized"));
      }
    }

    return response;
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(localStorage.getItem("studyflow_token"));

  const { data: user, isLoading, error } = useGetMe({
    query: {
      enabled: !!token,
      retry: false,
    }
  });

  const handleLogout = useCallback(() => {
    setToken(null);
    localStorage.removeItem("studyflow_token");
    queryClient.clear();
    setLocation("/login");
  }, [queryClient, setLocation]);

  useEffect(() => {
    window.addEventListener("auth-unauthorized", handleLogout);
    return () => window.removeEventListener("auth-unauthorized", handleLogout);
  }, [handleLogout]);

  useEffect(() => {
    if (error) {
      handleLogout();
    }
  }, [error, handleLogout]);

  const login = (newToken: string) => {
    localStorage.setItem("studyflow_token", newToken);
    setToken(newToken);
    queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
  };

  const logout = () => {
    localStorage.removeItem("studyflow_token");
    setToken(null);
    queryClient.clear();
    setLocation("/");
  };

  return (
    <AuthContext.Provider
      value={{
        user: user || null,
        isLoading: isLoading && !!token,
        login,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}
